import { create } from "zustand";
import axios from "axios";
import { toast } from "sonner";
import { getAxiosErorrMessage, getUnexpiredToken } from "@/utils/utils";
import { fetchFileTree } from "../codebaseSlice";
import { SignedInSessionResource } from "@clerk/types";
import { Database } from "@/utils/supabase.types";
import { useDesignCommandManager } from "./designCommandManagerStore";

export type ActivityItem =
  Database["public"]["Tables"]["activity_stream"]["Row"];

interface ActivityState {
  // The list is always sorted by timestamp descending
  activityStreamList: ActivityItem[];
  activityStreamLookup: Record<string, ActivityItem>;
  activityStreamLoadStatus: string;

  restoringActivityId: string | null;
  refetchActivityStreamTrigger: number;
  triggerRefetchActivityStream: () => void;

  restoreActivity: (
    dispatch: any,
    canvasId: string,
    activityId: string,
    activityStreamList: any[],
    session: SignedInSessionResource
  ) => Promise<void>;

  setActivities: (payload: ActivityItem[]) => void;
  onActivitySnapshot: (payload: {
    newItems: ActivityItem[];
    deletedItems: ActivityItem[];
  }) => void;
  setActivityStreamLoading: () => void;
  setActivityChangeUndone: (payload: {
    id: string;
    changeUndone: boolean;
  }) => void;
}

// Create the Zustand store with a more structured and type-safe approach
const useActivityStore = create<ActivityState>((set, get) => ({
  restoringActivityId: null,

  activityStreamList: [],
  activityStreamLookup: {},
  activityStreamLoadStatus: "idle",

  refetchActivityStreamTrigger: 0,
  triggerRefetchActivityStream: () =>
    set((state) => ({
      refetchActivityStreamTrigger: state.refetchActivityStreamTrigger + 1,
    })),

  restoreActivity: async (
    dispatch: any,
    canvasId: string,
    activityId: string,
    activityStreamList: any[],
    session: SignedInSessionResource
  ) => {
    set({ restoringActivityId: activityId });

    if (!session) {
      return;
    }

    const matchingActivityIndex = activityStreamList.findIndex(
      (activity) => activity.id === activityId
    );
    const matchingActivity = activityStreamList[matchingActivityIndex];

    if (!matchingActivity) {
      toast.error("Activity not found, please refresh the page");
      return;
    }

    // If the activity is undone, we need to redo it
    const isUndo = !matchingActivity.change_undone;

    // Calculate the expected steps count (delta) for sanity check
    let expectedStepsCount = 0;
    if (isUndo) {
      // Count activities from current (latest non-undone) back to the target activity
      expectedStepsCount = activityStreamList.filter(
        (activity, index) =>
          index < matchingActivityIndex && !activity.change_undone
      ).length;
    } else {
      // Count activities from target forward to current state
      expectedStepsCount = activityStreamList.filter(
        (activity, index) =>
          index > matchingActivityIndex && activity.change_undone
      ).length;
    }

    const body: {
      expectedStepsCount: number;
      undoRestoreActivityId?: string;
      redoRestoreActivityId?: string;
    } = { expectedStepsCount };
    if (isUndo) {
      body.undoRestoreActivityId = matchingActivity.id;
    } else {
      body.redoRestoreActivityId = matchingActivity.id;
    }

    await axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/parseAndMutate/activities/${
          isUndo ? "undoChangeToFiles" : "redoChangeToFiles"
        }`,
        body,
        {
          headers: {
            Authorization: `Bearer ${await getUnexpiredToken(session)}`,
          },
        }
      )
      .then(async (res) => {
        // After restoring, clear the undo/redo stack so it lines up with the activity stream
        useDesignCommandManager.getState().setUndoStack([]);
        useDesignCommandManager.getState().setRedoStack([]);

        get().triggerRefetchActivityStream();
        dispatch(
          fetchFileTree({
            session,
            canvasId,
          })
        );
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          `Failed to restore activity, please refresh the page : ${getAxiosErorrMessage(
            err
          )}`
        );
      })
      .finally(() => {
        set({ restoringActivityId: null });
      });
  },

  setActivities: (payload: ActivityItem[]) =>
    set((state) => {
      const activityStreamList: ActivityItem[] = [];
      const activityStreamLookup: Record<string, ActivityItem> = {};

      payload?.forEach((activity) => {
        activityStreamList.push(activity);
        activityStreamLookup[activity.id] = activity;
      });

      return {
        activityStreamList: activityStreamList.sort((a, b) =>
          b.timestamp?.localeCompare(a.timestamp)
        ),
        activityStreamLookup,
        activityStreamLoadStatus: "succeeded",
      };
    }),

  onActivitySnapshot: (payload: {
    newItems: ActivityItem[];
    deletedItems: ActivityItem[];
  }) =>
    set((state) => {
      const updatedActivities: Record<string, ActivityItem> = {};
      const deletedActivities = new Set<string>();
      const newActivityStreamList = [...state.activityStreamList];
      const newActivityStreamLookup = { ...state.activityStreamLookup };

      payload?.newItems?.forEach((activity) => {
        if (newActivityStreamLookup[activity.id]) {
          updatedActivities[activity.id] = activity;
        } else {
          newActivityStreamList.push(activity);
        }
        newActivityStreamLookup[activity.id] = activity;
      });

      payload?.deletedItems?.forEach((activity) => {
        deletedActivities.add(activity.id);
        delete newActivityStreamLookup[activity.id];
      });

      const filteredList = newActivityStreamList
        .filter((activity) => !deletedActivities.has(activity.id))
        .map((activity) => updatedActivities[activity.id] || activity)
        .sort((a, b) => b.timestamp?.localeCompare(a.timestamp));

      return {
        activityStreamList: filteredList,
        activityStreamLookup: newActivityStreamLookup,
      };
    }),

  setActivityStreamLoading: () => set({ activityStreamLoadStatus: "loading" }),

  setActivityChangeUndone: (payload: { id: string; changeUndone: boolean }) =>
    set((state) => {
      const newActivityStreamLookup = { ...state.activityStreamLookup };
      newActivityStreamLookup[payload.id] = {
        ...newActivityStreamLookup[payload.id],
        change_undone: payload.changeUndone,
      };

      const activityIndex = state.activityStreamList.findIndex(
        (activity) => activity.id === payload.id
      );

      const newActivityStreamList = [
        ...state.activityStreamList.slice(0, activityIndex),
        newActivityStreamLookup[payload.id],
        ...state.activityStreamList.slice(activityIndex + 1),
      ];

      return {
        activityStreamList: newActivityStreamList,
        activityStreamLookup: newActivityStreamLookup,
      };
    }),
}));

export default useActivityStore;
